import React, { useState, useEffect, useId, useRef } from 'react';
import {
  Incident,
  RemediationAction,
  acknowledgeContinuousIncident,
  resolveContinuousIncident,
  listIncidentRemediations,
  getContinuousIncident,
} from '../api';
import { incidentActionTarget } from './incidentActions';
import type { IncidentActionTarget } from './incidentActions';
import { humanize } from '../labels';
import { RemediationActionCard } from './RemediationActionCard';
import { Callout, Dialog, Subsection, Textarea } from './primitives';
import type { CalloutTone } from './primitives';
import { Field, StatusBadge } from './ui';
import { actionLabel } from './operation/actionLabels';

interface IncidentDetailModalProps {
  incident: Incident;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
  onNavigateToDeployment?: (deploymentId: string) => void;
  /** Opens the real workflow a suggested action names (UX-01). */
  onOpenAction?: (target: IncidentActionTarget) => void;
  /** The project's checks, to name the one an incident was raised from. */
  monitors?: { id: string; name: string }[];
}

interface Feedback {
  tone: CalloutTone;
  title?: string;
  text: string;
}

// The first usable suggested action leads at the default size; the rest are row actions.
const primaryClass = 'button button--primary';
const secondaryClass = 'button button--secondary button--small';

function formatTime(value: string) {
  return new Date(value).toLocaleString();
}

export const IncidentDetailModal: React.FC<IncidentDetailModalProps> = ({
  incident: listed,
  isOpen,
  onClose,
  onRefresh,
  onNavigateToDeployment,
  onOpenAction,
  monitors,
}) => {
  // The list carries the incident only; its timeline, correlations and the
  // suggested actions (decided from current facts) come from the detail.
  const [detail, setDetail] = useState<Incident | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  // Bumped by Retry: reads the detail and the suggested fixes again.
  const [reloadToken, setReloadToken] = useState(0);
  const incident = detail ?? listed;
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setDetailError(null);
    getContinuousIncident(listed.id)
      .then((full) => {
        if (!cancelled) setDetail(full);
      })
      .catch((err: unknown) => {
        if (!cancelled) setDetailError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, listed.id, reloadToken]);
  const [isResolving, setIsResolving] = useState(false);
  const [resolutionReason, setResolutionReason] = useState('');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState(false);
  const [remediations, setRemediations] = useState<RemediationAction[]>([]);
  // A failed read of the suggested fixes is said, never shown as "no fixes" (PR6).
  const [remediationError, setRemediationError] = useState<string | null>(null);
  const [remediationsLoaded, setRemediationsLoaded] = useState(false);
  const resolveFormId = useId();
  const resolveButtonRef = useRef<HTMLButtonElement>(null);
  const acknowledgeRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Buttons are disabled while a request runs, which drops focus on the page
  // behind the dialog. After the footer re-renders enabled, focus goes back
  // to the first of these that still exists.
  const [refocus, setRefocus] = useState<React.RefObject<HTMLButtonElement | null>[] | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setRemediationError(null);
    listIncidentRemediations(incident.id)
      .then((res) => {
        if (cancelled) return;
        setRemediations(res.actions || []);
        setRemediationsLoaded(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRemediations([]);
        setRemediationError(err instanceof Error && err.message ? err.message : 'No reason was given.');
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, incident.id, reloadToken]);

  useEffect(() => {
    if (!refocus || loading) return;
    refocus.map((ref) => ref.current).find((el) => el && el.isConnected && !el.disabled)?.focus();
    setRefocus(null);
  }, [refocus, loading]);

  if (!isOpen) return null;

  const handleAcknowledge = async () => {
    try {
      setLoading(true);
      await acknowledgeContinuousIncident(incident.id);
      setFeedback({ tone: 'success', text: 'Incident acknowledged.' });
      onRefresh();
      getContinuousIncident(incident.id).then(setDetail).catch(() => undefined);
    } catch (err: any) {
      setFeedback({ tone: 'danger', title: 'The incident was not acknowledged', text: err?.message || 'Failed to acknowledge' });
    } finally {
      setLoading(false);
      setRefocus([acknowledgeRef, closeRef]);
    }
  };

  const handleResolve = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await resolveContinuousIncident(incident.id, resolutionReason);
      setIsResolving(false);
      setFeedback({ tone: 'success', text: 'Incident resolved.' });
      onRefresh();
      getContinuousIncident(incident.id).then(setDetail).catch(() => undefined);
      setRefocus([resolveButtonRef, closeRef]);
    } catch (err: any) {
      setFeedback({ tone: 'danger', title: 'The incident was not resolved', text: err?.message || 'Failed to resolve' });
      setRefocus([confirmRef]);
    } finally {
      setLoading(false);
    }
  };

  const cancelResolve = () => {
    setIsResolving(false);
    // The Cancel button disappears with the form: hand focus back to the
    // button that opened it rather than dropping it on the page.
    setRefocus([resolveButtonRef, closeRef]);
  };

  const correlations = incident.correlations ?? [];
  const sourceMonitorName = monitors?.find((m) => m.id === incident.source_monitor_id)?.name;
  // An action is listed only when it resolves to a real destination; a
  // refused one stays listed with the server's reason (never as "safe").
  const actionRows = (incident.suggested_actions ?? [])
    .map((action) => ({ action, target: incidentActionTarget(action) }))
    .filter((row) => row.target !== null);
  const timeline = incident.timeline ?? [];
  const firstUsable = actionRows.findIndex(({ action, target }) => action.available && target && onOpenAction);
  // Resolving is the primary path only when nothing better is offered: with
  // a suggested workflow or fix, that is what the person came for (PR5).
  const hasWayForward = firstUsable >= 0 || remediations.length > 0;
  // Said only when both reads finished: an unloaded list is not an empty one.
  const nothingSuggested = detail !== null && !detailError && remediationsLoaded && !remediationError && actionRows.length === 0 && remediations.length === 0;
  const openSource = () => {
    onClose();
    onOpenAction?.({ section: 'monitoring', environmentId: incident.environment_id ?? undefined, params: { monitor: incident.source_monitor_id! }, label: actionLabel.investigate });
  };

  const footer = isResolving ? (
    <>
      <button type="button" className="button button--secondary" onClick={cancelResolve} disabled={loading}>
        Cancel
      </button>
      <button ref={confirmRef} type="submit" form={resolveFormId} className="button button--primary" disabled={loading}>
        {loading ? 'Resolving' : 'Confirm resolution'}
      </button>
    </>
  ) : (
    <>
      <button ref={closeRef} type="button" className="button button--secondary" onClick={onClose}>
        Close
      </button>
      {incident.status === 'open' && (
        <button ref={acknowledgeRef} type="button" className="button button--secondary" onClick={handleAcknowledge} disabled={loading}>
          Acknowledge
        </button>
      )}
      {incident.status !== 'resolved' && (
        <button
          ref={resolveButtonRef}
          type="button"
          className={`button ${hasWayForward ? 'button--secondary' : 'button--primary'}`}
          onClick={() => setIsResolving(true)}
          disabled={loading}
        >
          Resolve incident
        </button>
      )}
    </>
  );

  return (
    <Dialog open size="lg" title={incident.title} onClose={onClose} busy={loading} footer={footer} testId="incident-detail-dialog">
      {/* 1. Status and impact: how severe it is, since when, and on what. */}
      <div className="incident-meta" data-testid="incident-status">
        <StatusBadge kind="severity" value={incident.severity} />
        <StatusBadge kind="incident" value={incident.status} />
        <span className="incident-meta__fact type-meta">
          Opened <time dateTime={incident.opened_at}>{formatTime(incident.opened_at)}</time>
        </span>
        <span className="incident-meta__fact type-meta">
          Resource <code>{incident.primary_resource}</code>
        </span>
        {incident.root_cause_category && (
          <span className="incident-meta__fact type-meta">Category: {humanize(incident.root_cause_category)}</span>
        )}
      </div>

      {feedback && (
        <Callout tone={feedback.tone} title={feedback.title}>
          {feedback.text}
        </Callout>
      )}

      {isResolving && (
        <form id={resolveFormId} className="incident-resolve" onSubmit={handleResolve} aria-label="Resolve incident">
          <Field label="How was it resolved?" hint="Saved on the incident's timeline.">
            <Textarea
              required
              rows={3}
              autoFocus
              value={resolutionReason}
              onChange={(e) => setResolutionReason(e.target.value)}
              placeholder="Explain the resolution or recovery steps taken…"
            />
          </Field>
        </form>
      )}

      {/* 2. What to do: the workflows and fixes this incident has. */}
      {detailError || remediationError ? (
        <Callout
          tone="warning"
          role="status"
          title={detailError ? "The incident's details could not be loaded" : 'The suggested fixes could not be loaded'}
          testId="incident-load-error"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={() => setReloadToken((value) => value + 1)}>
              {actionLabel.retry}
            </button>
          }
        >
          {detailError ?? remediationError}
        </Callout>
      ) : null}

      {actionRows.length > 0 && (
        <Subsection
          title="What you can do"
          description="Nothing runs until you confirm in the workflow."
        >
          <ul className="incident-list" data-testid="incident-actions">
            {actionRows.map(({ action, target }, index) => (
              <li
                key={action.id}
                className={`incident-list__item${action.available ? '' : ' incident-list__item--unavailable'}`}
                data-testid={`incident-action-${action.action_type}`}
              >
                <div className="incident-list__text">
                  <span className="incident-list__title">
                    {action.title}
                    {!action.available && <StatusBadge kind="remediationAction" value="blocked" label="Not possible now" />}
                  </span>
                  {action.description ? <p className="incident-list__detail">{action.description}</p> : null}
                  {action.availability_reason ? <span className="type-meta">{action.availability_reason}</span> : null}
                </div>
                {action.available && target && onOpenAction ? (
                  <button
                    type="button"
                    className={index === firstUsable ? primaryClass : secondaryClass}
                    onClick={() => {
                      onClose();
                      onOpenAction(target);
                    }}
                  >
                    {target.label}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </Subsection>
      )}

      {remediations.length > 0 && (
        <Subsection
          title={`Suggested fixes (${remediations.length})`}
          description="Each runs only after a person approves it."
        >
          <div className="incident-fixes">
            {remediations.map((action) => (
              <RemediationActionCard
                key={action.id}
                action={action}
                onActionUpdated={(updated) => {
                  setRemediations((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
                  onRefresh();
                }}
              />
            ))}
          </div>
        </Subsection>
      )}

      {nothingSuggested ? (
        <p className="incident-none" data-testid="incident-no-actions">
          No fix or workflow to suggest
          {incident.source_monitor_id && onOpenAction ? (
            <>
              {' · '}
              Raised from {sourceMonitorName ? <strong>{sourceMonitorName}</strong> : 'a check'}{' '}
              <button type="button" className="button button--secondary button--small" onClick={openSource}>
                {actionLabel.investigate}
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      {/* 3. Details, on demand: what was seen, where it came from, how it is grouped. */}
      <details className="disclosure incident-details" data-testid="incident-details">
        <summary>{actionLabel.viewDetails}</summary>
        <div className="incident-details__body">
          {incident.description && <p className="incident-description">{incident.description}</p>}

          {incident.source_monitor_id && onOpenAction ? (
            <p className="incident-source" data-testid="incident-source-monitor">
              Raised by a check{sourceMonitorName ? <> — <strong>{sourceMonitorName}</strong></> : null}.{' '}
              <button type="button" className="link-button" onClick={openSource}>
                {actionLabel.investigate}
              </button>
            </p>
          ) : null}

          {incident.fingerprint ? (
            <p className="incident-source type-meta" data-testid="incident-fingerprint">
              <span title="Signals with the same key are grouped into this incident rather than opening a new one">Grouping key</span>{' '}
              <code title={incident.fingerprint}>{incident.fingerprint.slice(0, 12)}…</code>
            </p>
          ) : null}

          {correlations.length > 0 && (
            <Subsection title={`Related events and dependencies (${correlations.length})`}>
              <ul className="incident-list">
                {correlations.map((corr) => (
                  <li key={corr.id} className="incident-list__item">
                    <div className="incident-list__text">
                      <span className="incident-list__title">
                        {corr.title}
                        <span className="badge">{humanize(corr.confidence)}</span>
                      </span>
                      <p className="incident-list__detail">{corr.reason}</p>
                    </div>
                    {corr.correlated_type === 'deployment' && corr.correlated_id && onNavigateToDeployment && (
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        onClick={() => onNavigateToDeployment(corr.correlated_id!)}
                      >
                        View deployment
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </Subsection>
          )}

          {timeline.length > 0 && (
            <Subsection title={`Timeline (${timeline.length} ${timeline.length === 1 ? 'event' : 'events'})`}>
              <ol className="incident-timeline">
                {timeline.map((evt) => (
                  <li key={evt.id} className="incident-timeline__event">
                    <span>{evt.description}</span>
                    <span className="type-meta">
                      <time dateTime={evt.occurred_at}>{formatTime(evt.occurred_at)}</time>
                      {evt.actor ? ` by ${evt.actor}` : ''}
                    </span>
                  </li>
                ))}
              </ol>
            </Subsection>
          )}
        </div>
      </details>
    </Dialog>
  );
};
